import { Component, computed, inject, signal } from '@angular/core';
import { AppUpdateService } from '../services/app-update.service';

const UPDATE_UNAVAILABLE_MESSAGE = 'Update not available right now. Please try again later.';

/**
 * CMS version-gate prompt.
 * Optional (`forceUpdate` false): "Remind me later" snoozes until the update
 * check interval elapses or a newer build is ready (not cleared on Dashboard return).
 * Forced (`forceUpdate` true): no dismiss, and the service blocks leaving the dashboard.
 *
 * Stacking: `z-[100]` sits above dashboard dialogs (z-50/60), content dialogs (z-70),
 * camera/mic permission (z-50, see permission-modal), and telemetry (z-80/90). Offline (`z-[10000]`) and
 * toasts (`z-[9999]`) stay above for connectivity / system feedback.
 */
@Component({
  selector: 'app-update-prompt',
  standalone: true,
  template: `
    @if (visible()) {
      <div
        class="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md animate-fade-in"
        [attr.role]="isMandatory() ? 'alertdialog' : 'dialog'"
        aria-modal="true"
        [attr.aria-labelledby]="isMandatory() ? 'mandatory-update-title' : 'optional-update-title'"
        [attr.aria-describedby]="isMandatory() ? 'mandatory-update-desc' : 'optional-update-desc'"
      >
        <div
          class="relative w-full max-w-[460px] rounded-[32px] bg-[#222224] p-8 shadow-2xl"
          (click)="$event.stopPropagation()"
        >
          @if (!isMandatory()) {
            <!-- Top-Right Close Button (X) for Optional Update -->
            <button
              type="button"
              (click)="later()"
              aria-label="Close"
              class="hover:cursor-pointer absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <svg
                class="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          }

          <div class="flex flex-col items-center text-center">
            <!-- Title: JioType 900 / 32px / 32px / -3% -->
            <h2
              [id]="isMandatory() ? 'mandatory-update-title' : 'optional-update-title'"
              class="text-[32px] font-black leading-[32px] tracking-[-0.03em] text-white text-center"
            >
              {{ updateTitle() || (isMandatory() ? 'Update Required' : 'Update Available') }}
            </h2>

            <!-- Description: JioType 500 / 18px / 24px / -0.5% with 16px (mt-4) gap -->
            <p
              [id]="isMandatory() ? 'mandatory-update-desc' : 'optional-update-desc'"
              class="mt-4 text-[18px] font-medium leading-[24px] tracking-[-0.005em] text-white/[0.78] text-center"
            >
              {{
                updateSubtitle() ||
                  (isMandatory()
                    ? 'You must update JioPC to the latest version to access the dashboard.'
                    : 'Update now to get the latest features, security fixes, and performance improvements.')
              }}
            </p>

            <!-- Action CTAs with 32px (mt-8) margin top and 48px (h-[48px]) button height -->
            @if (isMandatory()) {
              <!-- Mandatory Update: Single full-width purple pill button -->
              <div class="mt-8 flex w-full items-center justify-center">
                <button
                  type="button"
                  (click)="updateNow()"
                  [disabled]="isApplying()"
                  class="hover:cursor-pointer w-full h-[48px] rounded-full bg-[#853DD6] hover:bg-[#7534BD] text-[18px] font-bold leading-[24px] tracking-[-0.005em] text-white shadow-lg shadow-purple-900/20 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-70 flex items-center justify-center cursor-pointer"
                >
                  {{ isApplying() ? 'Updating…' : 'Update' }}
                </button>
              </div>
            } @else {
              <!-- Optional Update: Two pill buttons side-by-side -->
              <div class="mt-8 flex w-full items-center justify-center gap-4">
                <button
                  type="button"
                  (click)="later()"
                  [disabled]="isApplying()"
                  class="hover:cursor-pointer flex-1 h-[48px] rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-[18px] font-bold leading-[24px] tracking-[-0.005em] text-white transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center cursor-pointer"
                >
                  Remind me later
                </button>
                <button
                  type="button"
                  (click)="updateNow()"
                  [disabled]="isApplying()"
                  class="hover:cursor-pointer flex-1 h-[48px] rounded-full bg-[#853DD6] hover:bg-[#7534BD] text-[18px] font-bold leading-[24px] tracking-[-0.005em] text-white shadow-lg shadow-purple-900/20 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-70 flex items-center justify-center cursor-pointer"
                >
                  {{ isApplying() ? 'Updating…' : 'Update JioPC' }}
                </button>
              </div>
            }

            @if (applyError()) {
              <p class="mt-4 text-[14px] font-medium leading-[20px] text-white/80" role="alert">
                {{ applyError() }}
              </p>
            }
          </div>
        </div>
      </div>
    }
  `,
})
export class UpdatePromptComponent {
  private readonly appUpdate = inject(AppUpdateService);

  readonly isApplying = signal(false);
  readonly applyError = signal<string | null>(null);

  readonly visible = computed(() => this.appUpdate.shouldShowPrompt());
  readonly isMandatory = computed(() => this.appUpdate.isForceUpdate());
  readonly updateTitle = computed(() => this.appUpdate.updateTitle());
  readonly updateSubtitle = computed(() => this.appUpdate.updateSubtitle());

  later(): void {
    if (this.isMandatory()) return;
    this.applyError.set(null);
    this.appUpdate.dismissOptional();
  }

  async updateNow(): Promise<void> {
    if (this.isApplying()) return;
    this.isApplying.set(true);
    this.applyError.set(null);
    try {
      const result = await this.appUpdate.applyUpdate();
      if (result === 'unavailable') this.applyError.set(UPDATE_UNAVAILABLE_MESSAGE);
    } finally {
      this.isApplying.set(false);
    }
  }
}
